import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { getOptionsTrades, OptionsTrade } from '../../services/optionsTraderService';
import { canAccessAutoTrader } from '../../lib/entitlements';

const OptionsTrades: React.FC = () => {
  const { user, token } = useAuth();
  const [trades, setTrades] = useState<OptionsTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed'>('all');

  const enabled = canAccessAutoTrader(user, 'tastytrade');

  // Reconciler-created rows (source='tt-manual') and PTS-adopted rows can arrive
  // with the "wrong" field names (symbol/pnl/strategy_name/contractSymbol) or
  // with strike/underlying missing entirely. Normalize before rendering so the
  // card doesn't render "SELL P / BUY P +$0 undefinedDTE" every time. Historical
  // rows are recovered from the OCC-encoded contractSymbol when present.
  const parseOcc = (sym: string | null | undefined): { underlying: string | null; strike: number | null; optionType: 'P' | 'C' | null; expiration: string | null } => {
    if (!sym) return { underlying: null, strike: null, optionType: null, expiration: null };
    const s = String(sym);
    // Underlying is the leading letters (may include / for futures like /ESU6).
    // All patterns are single-quantifier and anchored — no backtracking risk.
    /* eslint-disable security/detect-unsafe-regex */
    const uMatch = s.match(/^\/?([A-Z]{1,8})/);
    const eMatch = s.match(/(\d{6})[PC]/);
    const kMatch = s.match(/[PC](\d{1,8})(\.\d{1,4})?$/);
    /* eslint-enable security/detect-unsafe-regex */
    let expiration: string | null = null;
    if (eMatch) {
      const r = eMatch[1];
      expiration = `20${r.slice(0, 2)}-${r.slice(2, 4)}-${r.slice(4, 6)}`;
    }
    let strike: number | null = null;
    if (kMatch) {
      const intPart = kMatch[1];
      const n = parseFloat(intPart + (kMatch[2] || ''));
      // OCC equity encoding: 8-digit integer = dollars × 1000. Futures options
      // use short integer strikes (ES P6850). Divide only the long-integer form.
      strike = (!kMatch[2] && intPart.length >= 7) ? n / 1000 : n;
    }
    // Derive option type from the strike match — we already parsed it above,
    // so just read the letter that preceded the digits and skip a second regex.
    const typeMatch = s.match(/[PC](?=\d)/);
    const optionType: 'P' | 'C' | null = typeMatch ? (typeMatch[0] as 'P' | 'C') : null;
    return { underlying: uMatch ? uMatch[1] : null, strike, optionType, expiration };
  };

  const dteFromExp = (exp: string | null | undefined): number | null => {
    if (!exp) return null;
    const e = new Date(`${String(exp).slice(0, 10)}T00:00:00Z`).getTime();
    const now = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime();
    if (Number.isNaN(e)) return null;
    return Math.max(0, Math.round((e - now) / 86400000));
  };

  const normalizeTrade = (raw: any): any => {
    const t = { ...raw };
    // Contract symbol lives under several names depending on writer path.
    const occ = t.sellContractSymbol || t.contractSymbol || null;
    const parsed = parseOcc(occ);
    if (!t.underlying) t.underlying = t.symbol || parsed.underlying || null;
    if (t.sellStrike == null && parsed.strike != null && parsed.optionType === 'P') t.sellStrike = parsed.strike;
    if (t.expiration == null && parsed.expiration) t.expiration = parsed.expiration;
    if (t.realizedPnl == null && typeof t.pnl === 'number') t.realizedPnl = t.pnl;
    if (t.strategy == null && t.strategy_name) t.strategy = t.strategy_name;
    if (t.horizon == null) t.horizon = dteFromExp(t.expiration);
    return t;
  };

  useEffect(() => {
    if (!token || !enabled) return;
    setLoading(true);
    setFetchErr(null);
    getOptionsTrades(token, { limit: 100 })
      .then(setTrades)
      .catch((err) => {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load trades';
        setFetchErr(`Couldn't load trades: ${msg}`);
        console.error('OptionsTrades fetch error:', err);
      })
      .finally(() => setLoading(false));
  }, [token, enabled]);

  if (!enabled) {
    return (
      <div className="dashboard-container"><Navigation />
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
          <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700 }}>Options Trades</h1>
          <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
            <p style={{ color: '#9ca3af' }}>This feature is not enabled on your account.</p>
          </div>
        </div>
      </div>
    );
  }

  const filtered = trades
    .map(normalizeTrade)
    // After normalization, rows with no derivable underlying are ghost writes
    // (reconciler wrote a row without symbol OR OCC contract to parse) — hide
    // them rather than show an empty card. Very rare; audit trail preserved.
    .filter((t: any) => t.underlying)
    .filter((t: any) => {
      if (filter === 'open') return ['open', 'submitted', 'pending'].includes(t.status);
      if (filter === 'closed') return t.status === 'closed';
      return true;
    });

  const statusColor = (s: string) => {
    if (s === 'open') return '#4ade80';
    if (s === 'submitted') return '#fbbf24';
    if (s === 'closed') return '#6b7280';
    if (s === 'failed') return '#f87171';
    if (s === 'pending') return '#fbbf24';
    return '#6b7280';
  };

  const pnlColor = (pnl: number | null | undefined) => {
    if (pnl == null) return '#6b7280';
    return pnl >= 0 ? '#4ade80' : '#f87171';
  };

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '6px 16px', cursor: 'pointer', borderRadius: 6, fontSize: 13, fontWeight: 600,
    background: active ? '#6d28d9' : '#1f2937', color: active ? '#f9fafb' : '#6b7280',
    border: active ? '1px solid #7c3aed' : '1px solid #374151',
  });

  return (
    <div className="dashboard-container"><Navigation />
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: '#f9fafb', fontSize: 26, fontWeight: 700, margin: 0 }}>Options Trades</h1>
          <p style={{ color: '#9ca3af', fontSize: 14, marginTop: 4 }}>Credit spread trades from V2 research.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={tabStyle(filter === 'all')} onClick={() => setFilter('all')}>All ({trades.length})</span>
          <span style={tabStyle(filter === 'open')} onClick={() => setFilter('open')}>Open ({trades.filter(t => ['open','submitted','pending'].includes(t.status)).length})</span>
          <span style={tabStyle(filter === 'closed')} onClick={() => setFilter('closed')}>Closed ({trades.filter(t => t.status === 'closed').length})</span>
        </div>
      </div>

      {fetchErr && (
        <div style={{
          background: '#7f1d1d', color: '#fecaca', padding: '10px 14px',
          borderRadius: 6, marginBottom: 12, border: '1px solid #b91c1c',
          fontSize: 13,
        }}>{fetchErr}</div>
      )}

      {loading && <p style={{ color: '#6b7280' }}>Loading trades...</p>}

      {!loading && filtered.length === 0 && (
        <div style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: 28, textAlign: 'center' }}>
          <p style={{ color: '#9ca3af' }}>No trades found.</p>
        </div>
      )}

      {filtered.map(trade => (
        <div key={trade.tradeId} style={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 10, padding: '16px 20px', marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontWeight: 700, fontSize: 16, color: '#f9fafb' }}>{trade.underlying}</span>
                <span style={{ color: '#fbbf24', fontSize: 13 }}>
                  {trade.sellStrike != null && trade.buyStrike != null
                    ? `SELL ${trade.sellStrike}P / BUY ${trade.buyStrike}P`
                    : trade.sellStrike != null
                    ? `SELL ${trade.sellStrike}P`
                    : (trade as any).contractSymbol || 'option'}
                </span>
                <span style={{ color: '#6b7280', fontSize: 12 }}>exp {trade.expiration}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { label: trade.status.toUpperCase(), color: statusColor(trade.status) },
                  { label: trade.paperMode ? 'PAPER' : 'LIVE', color: trade.paperMode ? '#fbbf24' : '#4ade80' },
                  ...(trade.horizon != null ? [{ label: `${trade.horizon}DTE`, color: '#a78bfa' }] : []),
                  ...((trade as any).source === 'tt-manual' ? [{ label: 'MANUAL', color: '#6b7280' }] : []),
                  ...(trade.skewRegime ? [{ label: trade.skewRegime, color: '#6b7280' }] : []),
                ].map((b, i) => (
                  <span key={i} style={{ padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: b.color + '1a', color: b.color, border: `1px solid ${b.color}33` }}>
                    {b.label}
                  </span>
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              {trade.status === 'closed' ? (
                <>
                  <div style={{ fontSize: 20, fontWeight: 700, color: pnlColor(trade.realizedPnl) }}>
                    {(trade.realizedPnl || 0) >= 0 ? '+' : ''}${(trade.realizedPnl || 0).toFixed(0)}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{trade.exitReason?.replace(/_/g, ' ')} | {trade.holdDays}d</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 20, fontWeight: 700, color: pnlColor(trade.unrealizedPnl) }}>
                    {(trade.unrealizedPnl || 0) >= 0 ? '+' : ''}${(trade.unrealizedPnl || 0).toFixed(0)}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>unrealized</div>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginTop: 12, fontSize: 12 }}>
            {trade.credit != null && <div><span style={{ color: '#6b7280' }}>Credit:</span> <span style={{ color: '#4ade80' }}>${trade.credit}</span></div>}
            {trade.maxProfit != null && <div><span style={{ color: '#6b7280' }}>Max Profit:</span> <span style={{ color: '#f9fafb' }}>${trade.maxProfit}</span></div>}
            {trade.maxLoss != null && <div><span style={{ color: '#6b7280' }}>Max Loss:</span> <span style={{ color: '#f87171' }}>${trade.maxLoss}</span></div>}
            {trade.spotAtEntry != null && <div><span style={{ color: '#6b7280' }}>Spot @ Entry:</span> <span style={{ color: '#f9fafb' }}>${trade.spotAtEntry.toFixed(0)}</span></div>}
            {trade.delta != null && <div><span style={{ color: '#6b7280' }}>Delta:</span> <span style={{ color: '#f9fafb' }}>{trade.delta.toFixed(3)}</span></div>}
            {trade.iv != null && <div><span style={{ color: '#6b7280' }}>IV:</span> <span style={{ color: '#f9fafb' }}>{trade.iv.toFixed(1)}%</span></div>}
            {trade.probProfit != null && <div><span style={{ color: '#6b7280' }}>PoP:</span> <span style={{ color: '#f9fafb' }}>{trade.probProfit.toFixed(0)}%</span></div>}
            {trade.accountAtEntry && (
              <div><span style={{ color: '#6b7280' }}>Acct:</span> <span style={{ color: '#f9fafb' }}>{trade.accountAtEntry.accountNumber}</span></div>
            )}
          </div>

          {trade.dryRunResult && (
            <div style={{ marginTop: 8, fontSize: 11, color: trade.dryRunResult.valid ? '#4ade80' : '#fbbf24' }}>
              Dry-run: {trade.dryRunResult.valid ? 'Valid' : trade.dryRunResult.error || 'Failed'}
              {trade.dryRunResult.warnings?.length ? ` | ${trade.dryRunResult.warnings.join('; ')}` : ''}
            </div>
          )}

          <div style={{ marginTop: 6, fontSize: 11, color: '#4b5563' }}>
            {new Date(trade.createdAt).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET
          </div>
        </div>
      ))}
    </div>
    </div>
  );
};

export default OptionsTrades;
